import { useState } from 'react'
import { addDays } from 'date-fns/addDays'
import { addMonths } from 'date-fns/addMonths'
import {
  Button,
  cn,
  PlainA,
  RadioGroup,
  CheckboxWithLabel,
  Span,
  DatePicker,
  Select,
  Divider,
  TravelerSelection,
  type DateRange
} from '@pcln/horizon'
import AutocompleteWrapper from '@/components/AutocompleteWrapper/AutocompleteWrapper'
import { generateTimeSelectOptions } from '@/components/TimeSelect/TimeSelect'
import { DEFAULT_VALUES } from '@/components/Cars/HorizonForm/constants'
import usePackagesFormInitialValues from './usePackagesFormInitialValues'
import { useForm } from 'react-hook-form'
import type { TravelerState } from '@/types'
import { yupResolver } from '@hookform/resolvers/yup'
import useTabGroup from '@/hooks/useTabGroup'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'
import { TRAVELER_SELECTION_COLLISION_AVOIDANCE } from '@/components/SearchForm/constants'
import { formatPackageSearchHref } from '../SearchForm/utils'
import type { PackageFormTripType, PackagesFormStateType } from '../types'
import {
  getGridContainerClass,
  getCarPickupSlotClass,
  horizonPackagesForm
} from './HorizonPackagesForm.variants'
import {
  STAY_FLY,
  STAY_FLY_DRIVE,
  CHECKBOX_TEXT,
  RADIO_OPTIONS
} from './constants'
import { STAY_DRIVE } from '../SearchForm/TripTypes'
import usePackagesFormSubmitValues from './usePackagesFormSubmitValues'
import {
  isInfantLimitReachedPackages,
  isInfantOnInternationalTravel,
  searchFormSchema
} from '../SearchForm/validation'
import {
  getPartStaySelectedDates,
  handleDateRangeCalendarChange,
  handleSingleDateCalendarChange,
  isString
} from './utils'

export default function HorizonPackagesForm({
  onSubmit,
  pageName,
  useCompactLayout = false
}: {
  onSubmit: (
    pageName: 'homepage' | 'landing',
    data: PackagesFormStateType
  ) => void
  pageName: 'homepage' | 'landing'
  useCompactLayout?: boolean
}) {
  const { initialValues, isMobile, cguid, localeCode } =
    usePackagesFormInitialValues()
  const {
    radioButtons,
    originInput,
    destinationInput,
    timelineInput,
    passengersInput,
    advancedSearchSlot,
    checkBoxSlot,
    partStaySlot
  } = horizonPackagesForm()

  const {
    clearErrors,
    control,
    formState: { errors },
    handleSubmit,
    setValue
  } = useForm<PackagesFormStateType>({
    defaultValues: initialValues,
    mode: 'onSubmit',
    resolver: yupResolver(searchFormSchema),
    reValidateMode: 'onChange'
  })

  const {
    watchStartLocation,
    watchEndLocation,
    watchTravelers,
    watchTripType,
    watchRcLocation,
    watchStartDate,
    watchEndDate,
    watchHotelStartDate,
    watchHotelEndDate,
    watchPartialStay
  } = usePackagesFormSubmitValues({
    control,
    clearErrors,
    initialValues
  })

  const [packagesType, setPackagesType] = useState<PackageFormTripType>(
    initialValues.tripType
  )
  const [isHotelPartialStay, setIsHotelPartialStay] = useState(false)
  const [isCarPickupElsewhere, setIsCarPickupElsewhere] = useState(false)
  const [pickupTime, setPickupTime] = useState<string>(
    DEFAULT_VALUES.PICKUP_TIME
  )
  const [dropoffTime, setDropoffTime] = useState<string>(
    DEFAULT_VALUES.DROPOFF_TIME
  )
  const { registerTabElement } = useTabGroup()
  const hasOnlyHotelAndCar = packagesType === STAY_DRIVE
  const hasHotelIncluded = packagesType.includes('STAY')
  const hasHotelAndFlightIncluded =
    packagesType === STAY_FLY || packagesType === STAY_FLY_DRIVE
  const hasHotelPartStayIncluded =
    isHotelPartialStay && hasHotelAndFlightIncluded

  const handleSubmitHelper = handleSubmit((data: PackagesFormStateType) => {
    onSubmit(pageName, data)
    logSearchFormAnalytics(cguid, pageName, 'vacations', isMobile)
  })
  const currentDate = new Date()
  const calendarMaxMonth = addMonths(currentDate, 11)

  const [maxSelectableDate, setMaxSelectableDate] = useState<Date | undefined>()
  const [maxSelectablePartStayDate, setMaxSelectablePartStayDate] = useState<
    Date | undefined
  >()

  const [defaultMonth, setDefaultMonth] = useState<Date | undefined>(
    currentDate
  )
  const [defaultPartStayMonth, setPartStayDefaultMonth] = useState<
    Date | undefined
  >()
  const [defaultPickupMonth, setDefaultPickupMonth] = useState(currentDate)
  const [defaultDropoffMonth, setDefaultDropoffMonth] = useState(currentDate)

  return (
    <div className="w-auto">
      <form
        className="w-auto"
        aria-label="horizon-packages-search-form"
        onSubmit={handleSubmitHelper}
      >
        <div className="w-auto mx-auto">
          <div
            className={getGridContainerClass(
              packagesType,
              isHotelPartialStay,
              isCarPickupElsewhere,
              useCompactLayout
            )}
          >
            {/* Packages Type Radio Buttons */}
            <div className={radioButtons()}>
              <RadioGroup
                className="grid grid-rows-2 sm:grid-rows-1 grid-flow-col auto-cols-min gap-x-10 sm:gap-4"
                name="packagesType"
                defaultValue={packagesType}
                options={RADIO_OPTIONS}
                handleChange={(value: unknown) => {
                  if (isString(value)) {
                    setPackagesType(value)
                    setValue('tripType', value)
                  }
                }}
              />
            </div>
            {/* Start Location */}
            {!hasOnlyHotelAndCar && (
              <div className={originInput()}>
                <AutocompleteWrapper
                  searchKey="startLocation"
                  label="Where from?"
                  typeAheadEndpoint="packagesOrigin"
                  onItemSelect={item => {
                    setValue('startLocation', item)
                  }}
                  errorMessage={errors.startLocation?.message}
                  ref={(elem: HTMLInputElement) => {
                    registerTabElement(elem)
                  }}
                  initialSelectedItem={watchStartLocation ?? undefined}
                />
              </div>
            )}
            {/* End Location */}
            <div className={destinationInput()}>
              <AutocompleteWrapper
                searchKey="endLocation"
                label="Where to?"
                typeAheadEndpoint="hotels"
                onItemSelect={item => {
                  setValue('endLocation', item)
                }}
                errorMessage={errors.endLocation?.message}
                ref={(elem: HTMLInputElement) => {
                  registerTabElement(elem)
                }}
                initialSelectedItem={watchEndLocation ?? undefined}
              />
            </div>

            {/* Advanced Search - positioned here for tab order in compact mode */}
            {!hasOnlyHotelAndCar && (
              <div
                className={cn(
                  advancedSearchSlot(),
                  !useCompactLayout && 'lg:[grid-area:group]'
                )}
              >
                <PlainA
                  textStyle="body3"
                  className="font-bold"
                  iconRight="arrow_forward"
                  href={formatPackageSearchHref(
                    pageName,
                    watchTripType,
                    watchStartLocation,
                    watchEndLocation,
                    watchStartDate,
                    watchEndDate,
                    watchTravelers,
                    watchHotelStartDate,
                    watchHotelEndDate,
                    watchPartialStay
                  )}
                  target="_blank"
                >
                  Advanced Search (One-Way Flight, Multi-Hotel)
                </PlainA>
              </div>
            )}

            {/* Date Picker */}
            <div
              data-testid="date-picker-departing-returning"
              className={timelineInput()}
            >
              <DatePicker
                isMobile={isMobile}
                label="Departing - Returning"
                mode="range"
                numberOfMonths={isMobile ? 12 : 2}
                handleCalendarChange={(dates: DateRange) => {
                  handleDateRangeCalendarChange(
                    setValue,
                    'startDate',
                    'endDate',
                    setDefaultMonth,
                    dates
                  )
                  if (
                    hasHotelIncluded &&
                    dates?.from &&
                    dates?.to === undefined
                  ) {
                    setMaxSelectableDate(addDays(dates.from, 28))
                  } else {
                    setMaxSelectableDate(undefined)
                  }
                }}
                errorMessage={
                  errors.startDate?.message || errors.endDate?.message
                }
                inputRef={registerTabElement}
                disabled={{
                  before: currentDate,
                  after: maxSelectableDate
                }}
                startMonth={currentDate}
                endMonth={calendarMaxMonth}
                defaultMonth={defaultMonth}
                min={hasHotelIncluded ? 1 : 0}
              />
            </div>
            {/* Hotel Part Stay Checked - Date Picker (check-in/out)  */}
            {hasHotelPartStayIncluded && (
              <div
                data-testid="date-picker-hotel-part-stay"
                className={partStaySlot()}
              >
                <DatePicker
                  isMobile={isMobile}
                  label="Check-in - Check-out"
                  mode="range"
                  numberOfMonths={isMobile ? 12 : 2}
                  selected={getPartStaySelectedDates(
                    watchHotelStartDate,
                    watchHotelEndDate,
                    watchStartDate,
                    watchEndDate
                  )}
                  handleCalendarChange={(dates: DateRange) => {
                    handleDateRangeCalendarChange(
                      setValue,
                      'hotelStartDate',
                      'hotelEndDate',
                      setPartStayDefaultMonth,
                      dates
                    )
                    if (
                      hasHotelIncluded &&
                      dates?.from &&
                      dates?.to === undefined
                    ) {
                      setMaxSelectablePartStayDate(addDays(dates.from, 28))
                    } else {
                      setMaxSelectablePartStayDate(undefined)
                    }
                  }}
                  errorMessage={
                    errors.hotelStartDate?.message ||
                    errors.hotelEndDate?.message
                  }
                  inputRef={registerTabElement}
                  disabled={{
                    before: currentDate,
                    after: maxSelectablePartStayDate
                  }}
                  startMonth={currentDate}
                  endMonth={calendarMaxMonth}
                  defaultMonth={defaultPartStayMonth || defaultMonth}
                  min={1}
                />
              </div>
            )}
            {/* Travelers */}
            <div className={passengersInput()}>
              <TravelerSelection
                isMobile={isMobile}
                isHotel={hasOnlyHotelAndCar}
                initialValues={initialValues.travelers}
                roomsEnabled={hasHotelIncluded}
                ageSelectEnabled
                maxTravelers={8}
                maxChildren={6}
                tooltipErrorMessageId="travelerSelectionError"
                onChange={values => {
                  setValue('travelers', values as TravelerState)
                }}
                errorMessage={
                  isInfantOnInternationalTravel(
                    watchStartLocation,
                    watchEndLocation,
                    watchTravelers.childrenAges,
                    watchTripType
                  ) ||
                  isInfantLimitReachedPackages(
                    watchTravelers.adults,
                    watchTravelers.childrenAges,
                    watchTripType
                  ) ||
                  errors?.travelers?.childrenAges?.message
                }
                collisionAvoidance={TRAVELER_SELECTION_COLLISION_AVOIDANCE}
                disclaimer="You may only book a package for up to 8 travelers."
              />
            </div>
            {/* CheckBox - positioned here for tab order */}
            {hasHotelIncluded && (
              <div
                className={cn(
                  checkBoxSlot(),
                  !useCompactLayout && 'lg:[grid-area:group]'
                )}
              >
                <CheckboxWithLabel
                  checked={
                    packagesType === STAY_DRIVE
                      ? isCarPickupElsewhere
                      : isHotelPartialStay
                  }
                  handleChange={() => {
                    if (
                      packagesType === STAY_FLY ||
                      packagesType === STAY_FLY_DRIVE
                    ) {
                      setValue('partialStay', !isHotelPartialStay)
                      setIsHotelPartialStay(!isHotelPartialStay)
                      if (!isHotelPartialStay) {
                        setValue(
                          'hotelStartDate',
                          watchHotelStartDate || watchStartDate
                        )
                        setValue(
                          'hotelEndDate',
                          watchHotelEndDate || watchEndDate
                        )
                      }
                    }
                    if (packagesType === STAY_DRIVE) {
                      setValue('pickupCarSomewhere', !isCarPickupElsewhere)
                      setIsCarPickupElsewhere(!isCarPickupElsewhere)
                    }
                  }}
                  labelNode={
                    <Span textStyle="body3">{CHECKBOX_TEXT[packagesType]}</Span>
                  }
                  size="16"
                  name="checkbox-hotel-part-stay"
                  value="hotel"
                />
              </div>
            )}
            {/* Pickup Car Elsewhere */}
            {isCarPickupElsewhere && hasOnlyHotelAndCar && (
              <div
                className={getCarPickupSlotClass(
                  packagesType,
                  isHotelPartialStay,
                  isCarPickupElsewhere,
                  useCompactLayout
                )}
              >
                <div className="[grid-area:city]">
                  <AutocompleteWrapper
                    searchKey="cars"
                    label="City, Airport or Address"
                    typeAheadEndpoint="cars"
                    onItemSelect={item => {
                      setValue('rcLocation', item)
                    }}
                    errorMessage={errors.rcLocation?.message}
                    ref={(elem: HTMLInputElement) => {
                      registerTabElement(elem)
                    }}
                    initialSelectedItem={watchRcLocation ?? undefined}
                  />
                </div>
                <div className="**:translate-y-0 [grid-area:pickupDate]">
                  <DatePicker
                    isMobile={isMobile}
                    label="Pick-up"
                    mode="single"
                    numberOfMonths={isMobile ? 12 : 1}
                    handleCalendarChange={(date: Date) => {
                      handleSingleDateCalendarChange(
                        setValue,
                        'rcPickupDate',
                        setDefaultPickupMonth,
                        date
                      )
                    }}
                    errorMessage={errors.rcPickupDate?.message}
                    inputRef={registerTabElement}
                    disabled={{
                      before: currentDate
                    }}
                    startMonth={currentDate}
                    endMonth={calendarMaxMonth}
                    defaultMonth={defaultPickupMonth}
                  />
                </div>
                <div className="[grid-area:pickupTime]">
                  <Select
                    label="Pick-up Time"
                    isInset
                    menuItems={generateTimeSelectOptions(localeCode, true)}
                    initialValue={pickupTime}
                    onChange={item => {
                      setPickupTime(item)
                      setValue('rcPickupTime', item)
                    }}
                    placeholder="Pick-up Time"
                  />
                </div>
                <div className="**:translate-y-0 [grid-area:dropoffDate]">
                  <DatePicker
                    isMobile={isMobile}
                    label="Drop-off"
                    mode="single"
                    numberOfMonths={isMobile ? 12 : 1}
                    handleCalendarChange={(date: Date) => {
                      handleSingleDateCalendarChange(
                        setValue,
                        'rcDropoffDate',
                        setDefaultDropoffMonth,
                        date
                      )
                    }}
                    errorMessage={errors.rcDropoffDate?.message}
                    inputRef={registerTabElement}
                    disabled={{
                      before: currentDate
                    }}
                    startMonth={currentDate}
                    endMonth={calendarMaxMonth}
                    defaultMonth={defaultDropoffMonth}
                  />
                </div>
                <div className="[grid-area:dropoffTime]">
                  <Select
                    label="Drop-off Time"
                    isInset
                    menuItems={generateTimeSelectOptions(localeCode, true)}
                    initialValue={dropoffTime}
                    onChange={item => {
                      setDropoffTime(item)
                      setValue('rcDropoffTime', item)
                    }}
                    placeholder="Drop-off Time"
                  />
                </div>
              </div>
            )}
            {/* Search Button */}
            <Button
              buttonType="submit"
              type="primary"
              size="lg"
              className={cn(
                '[grid-area:submit] sm:[grid-area:submit]',
                !useCompactLayout && 'lg:[grid-area:unset]',
                isMobile ? 'fixed bottom-4 right-4 left-4' : ''
              )}
            >
              Find Your Trip
            </Button>
            {/* Divider - only shown on lg screens in non-compact mode */}
            {hasHotelAndFlightIncluded && !useCompactLayout && (
              <div className="hidden h-4 lg:[grid-area:group] lg:flex flex-row justify-center">
                <Divider direction="vertical" />
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  )
}
